import { useState } from 'react'
import { complianceAPI } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'

/**
 * Settings → the compliance export: the creator's posts (each with the
 * posting check run on it now, and where it went out) and locked calls (each
 * with its receipt) for a period, as one JSON file (backend
 * services/compliance_export). Blank dates mean the whole record.
 */
const ComplianceExport = () => {
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const backwards = Boolean(since && until && since > until)

  const download = async () => {
    setBusy(true)
    setError(null)
    try {
      const data = await complianceAPI.creatorExport({ since: since || undefined, until: until || undefined })
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `fincreator-compliance-${since || 'start'}-to-${until || 'today'}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't prepare the export."))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="bg-white rounded-lg shadow p-6" aria-labelledby="compliance-export-title" data-testid="compliance-export">
      <h2 id="compliance-export-title" className="text-lg font-semibold text-gray-900 mb-2">
        Compliance export
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Your posts and locked calls for a period, as one file: each post with the posting check run on it now and
        where it went out (deleted posts included and marked), and each call with its receipt and the public link
        that verifies it. For a regulator, a platform or a sponsor. It states what FinCreator holds; it isn&rsquo;t
        legal advice or a certification.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-700">
          From
          <input
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="ml-2 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
        </label>
        <label className="text-sm text-gray-700">
          To
          <input
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="ml-2 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={download}
          disabled={busy || backwards}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
        >
          {busy ? 'Preparing…' : 'Download compliance export'}
        </button>
      </div>
      {backwards && <p className="mt-2 text-sm text-amber-600">The period starts after it ends.</p>}
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-500">
          {error}
        </p>
      )}
    </section>
  )
}

export default ComplianceExport
